/**
 * Shopping List Service
 * Handles shopping list generation, consolidation, storage, and management
 */

import { 
  ShoppingList, 
  ShoppingListItem, 
  ShoppingListStatus,
  StoreSection,
  ShoppingListGenerationOptions,
  DEFAULT_GENERATION_OPTIONS,
  INGREDIENT_CATEGORIES,
  OriginalIngredient,
  ShoppingListShareData
} from '../types/shoppingList'
import { Recipe, Ingredient } from '../types/recipe'
import { anthropicService } from '../services/anthropicService'
import { combineIngredientAmounts, formatAmount } from '../utils/unitConversion'
import { clientLogger } from '../utils/clientLogger'

class ShoppingListService {
  private storageKey = 'talktoast_shopping_lists'
  private activeListTTL = 24 * 60 * 60 * 1000 // 24 hours for active lists
  private completedListTTL = 7 * 24 * 60 * 60 * 1000 // 7 days for completed lists
  private maxStoredLists = 10

  /**
   * Generate shopping list from an array of recipes using Claude consolidation
   */
  async generateShoppingList(
    recipes: Recipe[],
    mealPlanId?: string,
    options: ShoppingListGenerationOptions = DEFAULT_GENERATION_OPTIONS,
    testMode = false
  ): Promise<ShoppingList & { testData?: { originalIngredients: string[], consolidatedItems: any[] } }> {
    clientLogger.debug({ recipeCount: recipes.length, options, consolidateIngredients: options.consolidateIngredients }, 'Generating shopping list')
    
    if (!recipes || recipes.length === 0) {
      throw new Error('No recipes provided for shopping list generation')
    }
    
    const shoppingListId = `shopping_list_${Date.now()}`
    const now = new Date().toISOString()
    
    // Extract all shopping names from recipes, including amount and unit
    const allShoppingNames: string[] = []
    const ingredientMap: Map<string, Array<{ recipeId: string, recipeName: string, originalText: string }>> = new Map()

    // Water is a utility item that should never appear on a shopping list
    const isWater = (name: string) =>
      /^(tap\s+)?water$|^(cold|hot|warm|lukewarm|boiling|filtered|drinking)\s+water$|^water\s*,/i.test(name.trim())

    recipes.forEach(recipe => {
      recipe.ingredients.forEach((ingredient: Ingredient) => {
        // Use shoppingName if available, otherwise fallback to ingredient name
        const shoppingName = ingredient.shoppingName || ingredient.name
        if (shoppingName) {
          // #93: Skip water — it's a utility item, not something users buy
          if (isWater(shoppingName)) return

          // Build the full item string with amount and unit so Claude receives
          // weight/quantity info (e.g. "1.5 lbs boneless chicken breast") instead
          // of a bare name that causes it to fall back to vague guidance.
          const amountStr = ingredient.amount != null && ingredient.amount !== 0 ? String(ingredient.amount) : ''
          // #92: Skip unit when it duplicates the ingredient name (e.g. unit="mango", name="mango" → "1 mango mango")
          const rawUnit = ingredient.unit ? ingredient.unit.trim() : ''
          const unitStr = rawUnit.toLowerCase() === shoppingName.toLowerCase() ? '' : rawUnit
          const fullItemString = [amountStr, unitStr, shoppingName]
            .filter(part => part !== '')
            .join(' ')
            .trim()

          allShoppingNames.push(fullItemString)

          // Track original ingredients for reference.
          // Map is keyed by bare shoppingName (not the fullItemString) so that
          // post-consolidation lookups in getOriginalIngredientsForItem() still work.
          if (!ingredientMap.has(shoppingName)) {
            ingredientMap.set(shoppingName, [])
          }
          ingredientMap.get(shoppingName)!.push({
            recipeId: recipe.id,
            recipeName: recipe.name,
            originalText: ingredient.name
          })
        } else {
          clientLogger.warn({ ingredient }, 'Ingredient missing both shoppingName and name')
        }
      })
    })
    
    clientLogger.debug({ count: allShoppingNames.length }, 'Found shopping items')
    
    let shoppingItems: ShoppingListItem[]
    let consolidatedResult: any = null

    clientLogger.debug({ consolidateIngredients: options.consolidateIngredients, itemCount: allShoppingNames.length }, 'Consolidation check')

    if (options.consolidateIngredients && allShoppingNames.length > 0) {
      // Use Claude to consolidate intelligently
      clientLogger.debug({ itemCount: allShoppingNames.length }, 'Using Claude consolidation')
      try {
        consolidatedResult = await anthropicService.consolidateShoppingList(allShoppingNames)
        clientLogger.debug({}, 'Claude consolidation successful')
      } catch (error) {
        clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Claude consolidation failed')
        throw error
      }

      // Capture test data if in test mode
      if (testMode) {
        clientLogger.debug({ originalIngredients: allShoppingNames, consolidatedItems: consolidatedResult.items }, 'Test mode: capturing validation data')
      }
      
      shoppingItems = consolidatedResult.items.map((item: any, index: number) => {
        const originalRefs = this.getOriginalIngredientsForItem(item.name, ingredientMap)
        // Try to get the first original shoppingName for better Instacart matching
        const firstOriginalShoppingName = Array.from(ingredientMap.entries())
          .find(([key, _]) =>
            key.toLowerCase().includes(item.name.toLowerCase()) ||
            item.name.toLowerCase().includes(key.toLowerCase())
          )?.[0]

        // Parse structured guidance (Issue #38, #122)
        const totalQuantity = item.totalQuantity || item.amount
        // retailQuantity is the purchasable unit (e.g. "1 bulb", "2 bunches") — falls back to
        // totalQuantity when the ingredient is already retail-measurable (e.g. "2 lbs chicken").
        const retailQuantity = item.retailQuantity || totalQuantity
        const purchaseGuidance = item.purchaseGuidance || item.amount

        // Detect missing guidance for container items and add fallback
        const isContainerUnit = /\b(bag|bottle|jar|container|package|box)\b/i.test(purchaseGuidance)
        const hasGuidance = /\([^)]+\)/.test(purchaseGuidance) // Check for parentheses

        let finalPurchaseGuidance = purchaseGuidance
        if (isContainerUnit && !hasGuidance && originalRefs.length > 0) {
          // Fallback: Reconstruct from original ingredients
          const totalAmount = originalRefs.reduce((sum, ref) => sum + ref.originalAmount, 0)
          const unit = originalRefs[0]?.originalUnit || ''
          finalPurchaseGuidance = `${purchaseGuidance} (${totalAmount} ${unit} total)`
        }

        // Parse amount/unit from retailQuantity with range sanitization and warn-on-fallback (#122 CR).
        // Sanitize LLM range notation first: "1-2 bulbs" → "2 bulbs" (take ceiling/high end of
        // range so we don't under-buy). Without this the regex captures "1" and leaves "-2 bulbs"
        // as the unit string, producing a garbled Instacart payload.
        const sanitizedRetailQty = String(retailQuantity).replace(
          /(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/,
          (_match, _lo, hi) => hi
        )
        const retailAmountMatch = sanitizedRetailQty.match(/[\d.]+/)
        let parsedAmount: number
        let parsedUnit: string
        if (!retailAmountMatch) {
          // Warn so parse failures are visible in production logs. Include the raw string.
          console.warn(
            `[shoppingListService] retailQuantity parse failed — could not extract amount/unit. ` +
            `Raw retailQuantity: "${retailQuantity}". Falling back to totalQuantity: "${totalQuantity}".`
          )
          const fallbackStr = String(totalQuantity ?? '')
          const fallbackMatch = fallbackStr.match(/[\d.]+/)
          parsedAmount = fallbackMatch ? parseFloat(fallbackMatch[0]) : 1
          parsedUnit = fallbackStr.replace(/[\d.]+\s*/, '') || ''
        } else {
          parsedAmount = parseFloat(retailAmountMatch[0])
          parsedUnit = sanitizedRetailQty.replace(/[\d.]+\s*/, '') || ''
        }

        return {
          id: `item_${index}`,
          name: item.name,

          // NEW: Structured guidance fields
          totalQuantity: totalQuantity,
          // Persist the raw LLM-provided retail quantity string on the item (#122 CR).
          retailQuantity: retailQuantity,
          purchaseGuidance: finalPurchaseGuidance,
          recipeBreakdown: item.recipeBreakdown,

          // DEPRECATED: Legacy fields consumed by Instacart route (instacart.js item.amount / item.unit).
          // MUST be parsed from retailQuantity (e.g. "1 bulb", "2 bunches") so Instacart receives
          // purchasable retail units — NOT from totalQuantity which contains culinary sub-units
          // like "7 cloves" that Instacart would interpret as 7 individual purchasable items (#122).
          amount: parsedAmount,
          unit: parsedUnit,

          category: this.mapClaudeCategory(item.category),
          originalIngredients: originalRefs,
          checked: false,
          instacartName: firstOriginalShoppingName || item.name // Preserve original detailed name
        }
      })
    } else {
      // Fallback: create separate items without consolidation
      clientLogger.debug({ consolidateIngredients: options.consolidateIngredients, itemCount: allShoppingNames.length }, 'Using fallback (no consolidation)')
      shoppingItems = allShoppingNames.map((shoppingName, index) => ({
        id: `item_${index}`,
        name: shoppingName,
        amount: 1,
        unit: '',
        category: this.categorizeIngredient(shoppingName),
        originalIngredients: ingredientMap.get(shoppingName)?.map(orig => ({
          recipeId: orig.recipeId,
          recipeName: orig.recipeName,
          originalAmount: 1,
          originalUnit: ''
        })) || [],
        checked: false
      }))
    }
    
    const shoppingList: ShoppingList = {
      id: shoppingListId,
      name: this.generateShoppingListName(recipes),
      items: shoppingItems,
      mealPlanId,
      createdAt: now,
      updatedAt: now,
      status: 'active'
    }
    
    clientLogger.debug({ itemCount: shoppingItems.length }, 'Generated shopping list with consolidated items')
    
    // Add test data if in test mode
    if (testMode && options.consolidateIngredients && consolidatedResult) {
      return {
        ...shoppingList,
        testData: {
          originalIngredients: allShoppingNames,
          consolidatedItems: consolidatedResult.items
        }
      }
    }
    
    return shoppingList
  }

  /**
   * Consolidate ingredients by name and attempt unit conversion
   * @deprecated This method is kept for potential future use
   */
  // @ts-expect-error - Unused method kept for potential future use
  private consolidateIngredients(
    allIngredients: Array<{ ingredient: Ingredient, recipeId: string, recipeName: string }>,
    attemptUnitConversion: boolean
  ): ShoppingListItem[] {
    const consolidatedMap = new Map<string, ShoppingListItem>()
    
    allIngredients.forEach((item, index) => {
      clientLogger.debug({ ingredient: item.ingredient }, 'Processing ingredient')
      
      // Use the shopping-optimized name from Claude's response
      const shoppingName = item.ingredient.shoppingName
      
      const shoppingIngredient = {
        name: shoppingName,
        amount: item.ingredient.amount,
        unit: item.ingredient.unit
      }
      
      clientLogger.debug({ shoppingIngredient }, 'Shopping ingredient')
      
      const ingredientKey = shoppingIngredient.name.toLowerCase().trim()
      const existingItem = consolidatedMap.get(ingredientKey)
      
      if (!existingItem) {
        // First occurrence of this ingredient
        const newItem: ShoppingListItem = {
          id: `item_${Date.now()}_${index}`,
          name: shoppingIngredient.name,
          amount: shoppingIngredient.amount,
          unit: shoppingIngredient.unit,
          category: this.categorizeIngredient(shoppingIngredient.name),
          originalIngredients: [{
            recipeId: item.recipeId,
            recipeName: item.recipeName,
            originalAmount: item.ingredient.amount,
            originalUnit: item.ingredient.unit
          }],
          checked: false
        }
        
        consolidatedMap.set(ingredientKey, newItem)
      } else {
        // Ingredient already exists, try to combine
        const originalIngredient: OriginalIngredient = {
          recipeId: item.recipeId,
          recipeName: item.recipeName,
          originalAmount: item.ingredient.amount,
          originalUnit: item.ingredient.unit
        }
        
        if (attemptUnitConversion) {
          const conversionResult = combineIngredientAmounts(
            existingItem.amount,
            existingItem.unit,
            shoppingIngredient.amount,
            shoppingIngredient.unit,
            shoppingIngredient.name
          )
          
          if (conversionResult.success) {
            // Successfully combined
            existingItem.amount = conversionResult.amount
            existingItem.unit = conversionResult.unit
            existingItem.originalIngredients.push(originalIngredient)
            
            if (conversionResult.conversionNote) {
              existingItem.notes = (existingItem.notes || '') + 
                (existingItem.notes ? '; ' : '') + 
                conversionResult.conversionNote
            }
            
            clientLogger.debug({ name: item.ingredient.name, amount: formatAmount(conversionResult.amount), unit: conversionResult.unit }, 'Combined ingredient')
          } else {
            // Couldn't combine, create separate item
            const separateItem: ShoppingListItem = {
              id: `item_${Date.now()}_${index}_separate`,
              name: `${shoppingIngredient.name} (${shoppingIngredient.unit})`,
              amount: shoppingIngredient.amount,
              unit: shoppingIngredient.unit,
              category: this.categorizeIngredient(shoppingIngredient.name),
              originalIngredients: [originalIngredient],
              checked: false,
              notes: `Could not combine with other ${item.ingredient.name}`
            }
            
            consolidatedMap.set(`${ingredientKey}_${shoppingIngredient.unit}_${index}`, separateItem)
            clientLogger.debug({ name: item.ingredient.name }, 'Could not combine, created separate item')
          }
        } else {
          // No unit conversion, just add amounts if units match
          if (existingItem.unit.toLowerCase() === shoppingIngredient.unit.toLowerCase()) {
            existingItem.amount += shoppingIngredient.amount
            existingItem.originalIngredients.push(originalIngredient)
          } else {
            // Different units, create separate item
            const separateItem: ShoppingListItem = {
              id: `item_${Date.now()}_${index}_separate`,
              name: `${shoppingIngredient.name} (${shoppingIngredient.unit})`,
              amount: shoppingIngredient.amount,
              unit: shoppingIngredient.unit,
              category: this.categorizeIngredient(shoppingIngredient.name),
              originalIngredients: [originalIngredient],
              checked: false
            }
            
            consolidatedMap.set(`${ingredientKey}_${shoppingIngredient.unit}_${index}`, separateItem)
          }
        }
      }
    })
    
    return Array.from(consolidatedMap.values())
  }


  /**
   * Map Claude's category names to our StoreSection enum
   */
  private mapClaudeCategory(claudeCategory: string): StoreSection {
    const categoryMap: Record<string, StoreSection> = {
      'produce': StoreSection.PRODUCE,
      'meat': StoreSection.MEAT,
      'seafood': StoreSection.SEAFOOD,
      'dairy': StoreSection.DAIRY,
      'pantry': StoreSection.PANTRY,
      'spices': StoreSection.SPICES,
      'frozen': StoreSection.FROZEN,
      'bakery': StoreSection.BAKERY,
      'beverages': StoreSection.BEVERAGES,
      'other': StoreSection.OTHER
    }
    
    return categoryMap[claudeCategory.toLowerCase()] || StoreSection.OTHER
  }

  /**
   * Get original ingredient references for a consolidated item
   */
  private getOriginalIngredientsForItem(
    itemName: string, 
    ingredientMap: Map<string, Array<{ recipeId: string, recipeName: string, originalText: string }>>
  ): OriginalIngredient[] {
    // Try to find exact matches first
    for (const [shoppingName, originals] of ingredientMap.entries()) {
      if (shoppingName.toLowerCase().includes(itemName.toLowerCase()) || 
          itemName.toLowerCase().includes(shoppingName.toLowerCase())) {
        return originals.map(orig => ({
          recipeId: orig.recipeId,
          recipeName: orig.recipeName,
          originalAmount: 1,
          originalUnit: ''
        }))
      }
    }
    
    // Return empty array if no match found
    return []
  }

  /**
   * Categorize an ingredient into a store section
   */
  private categorizeIngredient(ingredientName: string): StoreSection {
    const key = ingredientName.toLowerCase().trim()
    
    // Check exact matches first
    if (INGREDIENT_CATEGORIES[key]) {
      return INGREDIENT_CATEGORIES[key]
    }
    
    // Check for partial matches (e.g., "red onion" contains "onion")
    for (const [categoryKey, section] of Object.entries(INGREDIENT_CATEGORIES)) {
      if (key.includes(categoryKey) || categoryKey.includes(key)) {
        return section
      }
    }
    
    // Default to OTHER if no category found
    return StoreSection.OTHER
  }

  /**
   * Get sort order for store sections
   * @deprecated This method is kept for potential future use
   */
  // @ts-expect-error - Unused method kept for potential future use
  private getCategoryOrder(category: StoreSection): number {
    const order: Record<StoreSection, number> = {
      [StoreSection.PRODUCE]: 1,
      [StoreSection.MEAT]: 2,
      [StoreSection.SEAFOOD]: 3,
      [StoreSection.DAIRY]: 4,
      [StoreSection.PANTRY]: 5,
      [StoreSection.SPICES]: 6,
      [StoreSection.FROZEN]: 7,
      [StoreSection.BAKERY]: 8,
      [StoreSection.BEVERAGES]: 9,
      [StoreSection.OTHER]: 10
    }
    
    return order[category] || 10
  }

  /**
   * Generate a descriptive name for the shopping list
   */
  private generateShoppingListName(recipes: Recipe[]): string {
    const now = new Date()
    const dateStr = now.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    })
    
    if (recipes.length === 1) {
      return `${recipes[0]?.name || 'Recipe'} - ${dateStr}`
    } else if (recipes.length <= 3) {
      const recipeNames = recipes.map(r => r.name.split(' ')[0]).join(', ')
      return `${recipeNames} - ${dateStr}`
    } else {
      return `Meal Plan (${recipes.length} recipes) - ${dateStr}`
    }
  }

  /**
   * Save shopping list to localStorage
   */
  saveShoppingList(shoppingList: ShoppingList, userId: string): void {
    try {
      const storageKey = `${this.storageKey}_${userId}`
      const stored = localStorage.getItem(storageKey)
      let savedLists: ShoppingList[] = stored ? JSON.parse(stored) : []
      
      // Update existing or add new
      const existingIndex = savedLists.findIndex(list => list.id === shoppingList.id)
      if (existingIndex >= 0) {
        savedLists[existingIndex] = {
          ...shoppingList,
          updatedAt: new Date().toISOString()
        }
      } else {
        savedLists.push(shoppingList)
      }
      
      // Clean up old lists
      this.cleanupOldLists(savedLists)
      
      // Keep only most recent lists
      savedLists.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      savedLists = savedLists.slice(0, this.maxStoredLists)
      
      localStorage.setItem(storageKey, JSON.stringify(savedLists))
      
      clientLogger.debug({ name: shoppingList.name }, 'Saved shopping list')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error saving shopping list')
    }
  }

  /**
   * Load shopping lists from localStorage
   */
  loadShoppingLists(userId: string): ShoppingList[] {
    try {
      const storageKey = `${this.storageKey}_${userId}`
      const stored = localStorage.getItem(storageKey)
      
      if (!stored) return []
      
      const savedLists: ShoppingList[] = JSON.parse(stored)
      
      // Clean up expired lists
      const validLists = savedLists.filter(list => !this.isListExpired(list))
      
      // Save cleaned list back if we removed any
      if (validLists.length !== savedLists.length) {
        localStorage.setItem(storageKey, JSON.stringify(validLists))
      }
      
      // Sort by creation date (newest first)
      validLists.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      
      clientLogger.debug({ count: validLists.length }, 'Loaded shopping lists')
      return validLists
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error loading shopping lists')
      return []
    }
  }

  /**
   * Get active shopping list (most recent non-completed)
   */
  getActiveShoppingList(userId: string): ShoppingList | null {
    const lists = this.loadShoppingLists(userId)
    return lists.find(list => list.status === 'active' || list.status === 'shopping') || null
  }

  /**
   * Update item quantity with validation
   */
  updateItemQuantity(
    shoppingListId: string,
    itemId: string,
    newAmount: number,
    userId: string
  ): void {
    clientLogger.debug({ shoppingListId, itemId, newAmount }, 'Updating item quantity')

    // Validate amount
    if (newAmount < 0) {
      clientLogger.warn({ newAmount }, 'Invalid amount, setting to 0')
      newAmount = 0
    }
    
    const updates: Partial<ShoppingListItem> = {
      amount: newAmount
    }
    
    // If quantity is 0, auto-check the item (user doesn't need to buy)
    if (newAmount === 0) {
      updates.checked = true
    }
    
    this.updateShoppingListItem(shoppingListId, itemId, updates, userId)
  }

  /**
   * Update shopping list item (toggle checked, update amount, etc.)
   */
  updateShoppingListItem(
    shoppingListId: string,
    itemId: string,
    updates: Partial<ShoppingListItem>,
    userId: string
  ): void {
    try {
      const lists = this.loadShoppingLists(userId)
      const listIndex = lists.findIndex(list => list.id === shoppingListId)
      
      if (listIndex === -1) {
        clientLogger.warn({ shoppingListId }, 'Shopping list not found')
        return
      }

      const list = lists[listIndex]
      if (!list) {
        clientLogger.warn({ shoppingListId }, 'Shopping list not found')
        return
      }

      const itemIndex = list.items.findIndex(item => item.id === itemId)

      if (itemIndex === -1) {
        clientLogger.warn({ itemId }, 'Shopping list item not found')
        return
      }

      const currentItem = list.items[itemIndex]
      if (!currentItem) {
        clientLogger.warn({ itemId }, 'Shopping list item not found')
        return
      }

      // Update item
      list.items[itemIndex] = { ...currentItem, ...updates } as ShoppingListItem
      list.updatedAt = new Date().toISOString()

      // Auto-update list status based on completion
      if (updates.checked !== undefined) {
        this.updateListStatusBasedOnItems(list)
      }

      this.saveShoppingList(list, userId)
      
      clientLogger.debug({ itemId }, 'Updated item')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error updating shopping list item')
    }
  }

  /**
   * Update shopping list status
   */
  updateShoppingListStatus(
    shoppingListId: string,
    status: ShoppingListStatus,
    userId: string
  ): void {
    try {
      const lists = this.loadShoppingLists(userId)
      const listIndex = lists.findIndex(list => list.id === shoppingListId)
      
      if (listIndex === -1) {
        clientLogger.warn({ shoppingListId }, 'Shopping list not found')
        return
      }

      const list = lists[listIndex]
      if (!list) {
        clientLogger.warn({ shoppingListId }, 'Shopping list not found')
        return
      }

      list.status = status
      list.updatedAt = new Date().toISOString()

      if (status === 'completed') {
        list.completedAt = new Date().toISOString()
      }

      this.saveShoppingList(list, userId)
      
      clientLogger.debug({ status }, 'Updated list status')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error updating shopping list status')
    }
  }

  /**
   * Delete shopping list
   */
  deleteShoppingList(shoppingListId: string, userId: string): void {
    try {
      const storageKey = `${this.storageKey}_${userId}`
      const stored = localStorage.getItem(storageKey)
      
      if (!stored) return
      
      const savedLists: ShoppingList[] = JSON.parse(stored)
      const filteredLists = savedLists.filter(list => list.id !== shoppingListId)
      
      localStorage.setItem(storageKey, JSON.stringify(filteredLists))
      
      clientLogger.debug({ shoppingListId }, 'Deleted shopping list')
    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error deleting shopping list')
    }
  }

  /**
   * Generate sharing data for a shopping list
   */
  generateShareData(shoppingList: ShoppingList): ShoppingListShareData {
    const itemsByCategory = this.groupItemsByCategory(shoppingList.items)
    
    let shareText = `🛒 ${shoppingList.name}\n\n`
    
    Object.entries(itemsByCategory).forEach(([category, items]) => {
      if (items.length > 0) {
        const categoryInfo = this.getCategoryInfo(category as StoreSection)
        shareText += `${categoryInfo.icon} ${categoryInfo.label}:\n`
        
        items.forEach(item => {
          const amountStr = formatAmount(item.amount)
          const checkBox = item.checked ? '✅' : '☐'
          shareText += `${checkBox} ${amountStr} ${item.unit} ${item.name}\n`
        })
        
        shareText += '\n'
      }
    })
    
    shareText += `Generated by TalkToast • ${new Date(shoppingList.createdAt).toLocaleDateString()}`
    
    return {
      title: shoppingList.name,
      text: shareText,
    }
  }

  /**
   * Group shopping list items by store category
   */
  groupItemsByCategory(items: ShoppingListItem[]): Record<string, ShoppingListItem[]> {
    const grouped: Record<string, ShoppingListItem[]> = {}
    
    items.forEach(item => {
      const category = item.category
      if (!grouped[category]) {
        grouped[category] = []
      }
      grouped[category].push(item)
    })
    
    return grouped
  }

  /**
   * Get display information for a store category
   */
  private getCategoryInfo(category: StoreSection): { label: string; icon: string } {
    const categoryMap = {
      [StoreSection.PRODUCE]: { label: 'Produce', icon: '🥕' },
      [StoreSection.MEAT]: { label: 'Meat & Poultry', icon: '🥩' },
      [StoreSection.SEAFOOD]: { label: 'Seafood', icon: '🐟' },
      [StoreSection.DAIRY]: { label: 'Dairy & Eggs', icon: '🥛' },
      [StoreSection.PANTRY]: { label: 'Pantry & Dry Goods', icon: '🥫' },
      [StoreSection.SPICES]: { label: 'Spices & Seasonings', icon: '🧂' },
      [StoreSection.FROZEN]: { label: 'Frozen', icon: '🧊' },
      [StoreSection.BAKERY]: { label: 'Bakery', icon: '🥖' },
      [StoreSection.BEVERAGES]: { label: 'Beverages', icon: '🥤' },
      [StoreSection.OTHER]: { label: 'Other', icon: '🛒' },
    }
    
    return categoryMap[category] || categoryMap[StoreSection.OTHER]
  }

  /**
   * Check if a shopping list has expired based on TTL
   */
  private isListExpired(list: ShoppingList): boolean {
    const now = Date.now()
    const listTime = new Date(list.completedAt || list.updatedAt).getTime()
    
    const ttl = list.status === 'completed' || list.status === 'archived' 
      ? this.completedListTTL 
      : this.activeListTTL
    
    return (now - listTime) > ttl
  }

  /**
   * Clean up old/expired shopping lists
   */
  private cleanupOldLists(lists: ShoppingList[]): void {
    const now = Date.now()
    
    lists.forEach(list => {
      const listTime = new Date(list.completedAt || list.updatedAt).getTime()
      const ttl = list.status === 'completed' || list.status === 'archived' 
        ? this.completedListTTL 
        : this.activeListTTL
      
      if ((now - listTime) > ttl) {
        clientLogger.debug({ name: list.name }, 'Marking expired list for cleanup')
      }
    })
  }

  /**
   * Update list status based on item completion
   */
  private updateListStatusBasedOnItems(list: ShoppingList): void {
    const totalItems = list.items.length
    const checkedItems = list.items.filter(item => item.checked).length
    
    if (checkedItems === totalItems && totalItems > 0) {
      // All items checked
      if (list.status !== 'completed') {
        list.status = 'completed'
        list.completedAt = new Date().toISOString()
      }
    } else if (checkedItems > 0) {
      // Some items checked
      if (list.status === 'active') {
        list.status = 'shopping'
      }
    } else {
      // No items checked
      if (list.status === 'shopping') {
        list.status = 'active'
      }
    }
  }

  /**
   * Get shopping list statistics
   */
  getShoppingListStats(shoppingList: ShoppingList): {
    totalItems: number
    checkedItems: number
    completionPercentage: number
    itemsByCategory: Record<string, number>
  } {
    const totalItems = shoppingList.items.length
    const checkedItems = shoppingList.items.filter(item => item.checked).length
    const completionPercentage = totalItems > 0 ? Math.round((checkedItems / totalItems) * 100) : 0
    
    const itemsByCategory: Record<string, number> = {}
    shoppingList.items.forEach(item => {
      const category = item.category
      itemsByCategory[category] = (itemsByCategory[category] || 0) + 1
    })
    
    return {
      totalItems,
      checkedItems,
      completionPercentage,
      itemsByCategory
    }
  }
}

export const shoppingListService = new ShoppingListService()
export default shoppingListService